import { Actioner, BuyFlowActionKeys } from "@services/actioner";
import type { ComponentType } from "react";

import {
	type AddonProduct,
	OnboardingDetails,
	PlanSummary,
	SIMTypeEnum,
	type DeliveryProduct,
	type Campaign,
	type PlanProduct,
	type BoosterProduct,
	type IAuthResponse,
	type ErrorResponse,
} from "@services/sdk";
import ProductGrouper from "@services/sdk/helpers/product-grouper";
import useProductsDataProvider from "@services/sdk/hooks/useProductsDataProvider";
import Result, {
	type PlanSummaryResult,
	type CampaignResult,
} from "@services/sdk/models/result";
import { getProductId } from "@utils/funnel";
import { getSimChannelFromURL } from "@utils/getSimChannelFromURL";
import { getSimProductIdFromUrl } from "@utils/getSimProductIdFromUrl";
import { reportError } from "@utils/errorReporting";
import { safeJsonParse } from "@utils/safeJsonParse";
import type { ReactNode } from "react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useAtom } from "jotai";
import {
	clientRefAtom,
	externalIdAtom,
	isHydratedAtom,
	selectedProductAtom,
	promoCodeAtom,
	selectedProductsAtom,
	deliveryMethodAtom,
	planAtom,
	simTypeAtom,
} from "@contexts/buyAtoms";

interface BuyContextType {
	plan: PlanProduct;
	products: (PlanProduct | DeliveryProduct | AddonProduct | BoosterProduct)[];
	planSummary: PlanSummary;
	onboard: (
		submittedOnboardingDetails: OnboardingDetails,
	) => Promise<Result<IAuthResponse, ErrorResponse> | Result<string, any>>;
	selectProduct: (product: AddonProduct) => void;
	deselectProduct: (product: AddonProduct) => void;
	selectedProducts: AddonProduct[];
	isCampaignApplied: (campaign: Campaign) => boolean;
	checkPromoCodeAndApply: (promocode: string) => Promise<CampaignResult>;
	promoCodeApplied: boolean;
	removePromoCode: () => void;
	fetchPlanSummary: (
		products: (PlanProduct | DeliveryProduct | AddonProduct | BoosterProduct)[],
		promo: Campaign | null,
	) => Promise<PlanSummaryResult>;
	webBuyStart: number;
	simType: SIMTypeEnum;
	isEsimSelected: boolean;
	selectSIMType: (type: SIMTypeEnum) => void;
	isFetchingSummary: boolean;
	setFormStartedEventFired: (fired: boolean) => void;
	formStartedEventFired: boolean;
}

export const BuyContext = React.createContext<BuyContextType | undefined>(
	undefined,
);

const initialState = {
	isHydrated: false,
	products: [],
	planSummary: PlanSummary.ConstructEmpty(),
	onboardingDetails: OnboardingDetails.ConstructEmpty(),
};

interface BuyContextProviderProps {
	children: ReactNode;
}
export const BuyContextProvider = ({ children }: BuyContextProviderProps) => {
	const [clientRef] = useAtom(clientRefAtom);
	const [, setIsHydrated] = useAtom(isHydratedAtom);
	const [selectedProduct, setSelectedProduct] = useAtom(selectedProductAtom);
	const [selectedProducts, setSelectedProducts] = useAtom(selectedProductsAtom);
	const [deliveryMethod, setDeliveryMethod] = useAtom(deliveryMethodAtom);
	const [plan, setPlan] = useAtom(planAtom);
	const [simType, setSimType] = useAtom(simTypeAtom);
	const [promoCode, setPromoCode] = useAtom(promoCodeAtom);
	const [, setExternalId] = useAtom(externalIdAtom);

	const isEsimSelected = simType === SIMTypeEnum.ESIM;

	const { getProducts } = useProductsDataProvider(clientRef);
	const [webBuyStart] = useState(Date.now());

	const [isFetchingSummary, setIsFetchingSummary] = useState(false);
	const [products, setProducts] = useState<
		(PlanProduct | DeliveryProduct | AddonProduct | BoosterProduct)[]
	>(initialState.products);

	const [planSummary, setPlanSummary] = useState(initialState.planSummary);
	const [formStartedEventFired, setFormStartedEventFired] = useState(false);
	const [promoCodeApplied, setPromoCodeApplied] = useState(false);

	// Listen for BUY_SET_BRAZE_ID action to store externalId in atom
	useEffect(() => {
		const listener = Actioner.addListener(
			BuyFlowActionKeys.BUY_SET_BRAZE_ID,
			(payload) => {
				if (payload.externalId) {
					setExternalId(payload.externalId);
				}
			},
		);

		return () => {
			listener.remove();
		};
	}, [setExternalId]);

	useEffect(() => {
		async function getProductIdFromUrl() {
			const response: number = await getProductId();
			setSelectedProduct(response);
		}
		getProductIdFromUrl();
	}, [setSelectedProduct]);

	const selectProduct = (product: AddonProduct) => {
		if (
			product.responseData?.id === null ||
			product.responseData?.id === undefined
		) {
			return;
		}

		setSelectedProducts({
			...selectedProducts,
			[String(product.responseData.id)]: product,
		});

		Actioner.trigger(Actioner.keyDefs.BUY_SELECT_PRODUCT, {
			id: product?.responseData?.id,
			name: product?.responseData?.name,
			price: product?.responseData?.price,
			type: product?.responseData?.type,
		});

		if (product.responseData.id === 4) {
			Actioner.trigger(Actioner.keyDefs.USE_ADD_INTL_ROAMING);
		}
	};

	const deselectProduct = (product: AddonProduct) => {
		if (
			product.responseData?.id === null ||
			product.responseData?.id === undefined
		) {
			return;
		}

		const selectedProductsDeepCopy = JSON.parse(
			JSON.stringify(selectedProducts),
		);
		delete selectedProductsDeepCopy[String(product.responseData.id)];
		setSelectedProducts(selectedProductsDeepCopy);

		Actioner.trigger(Actioner.keyDefs.BUY_DESELECT_PRODUCT, {
			id: product?.responseData?.id,
			name: product?.responseData?.name,
			price: product?.responseData?.price,
			type: product?.responseData?.type,
		});
	};

	const selectSIMType = (type: SIMTypeEnum) => {
		setSimType(type);
	};

	const onboard = async (submittedOnboardingDetails: OnboardingDetails) => {
		Actioner.trigger(Actioner.keyDefs.PERFORMANCE_TRACE_START, {
			value: "ONBOARDING_TIMER",
		});

		const onboardResult = await clientRef.onboard(submittedOnboardingDetails);

		Actioner.trigger(Actioner.keyDefs.PERFORMANCE_TRACE_END);

		if (!onboardResult.isOk()) {
			reportError(onboardResult.getFailure(), {
				route: "/buy",
				flowStep: window.currentBuyFlowStep,
				component: "BuyContext",
				action: "onboard",
				metadata: {
					simType: submittedOnboardingDetails.simType,
					hasPromoCode: Boolean(submittedOnboardingDetails.promoCode),
				},
			});
			Actioner.trigger(Actioner.keyDefs.ERROR, onboardResult.getFailure());
			return onboardResult;
		}

		const { accountId } = onboardResult.getSuccess();

		Actioner.trigger(Actioner.keyDefs.BUY_SET_ACCOUNT_ID, {
			value: accountId,
		});

		const { promoCode: promo, simType } = submittedOnboardingDetails;

		const { discountedPrice, products: purchasedProducts } = planSummary;

		Actioner.trigger(Actioner.keyDefs.BUY_SUCCESS, {
			branchContentParams: {
				contentDescription: "Purchase_CorePlan_App",
				contentMetadata: {
					price: discountedPrice,
					productName: purchasedProducts.map((product) => product.name).join(),
					customMetadata: {
						accountNumber: accountId?.toString(),
						promoCode: promo?.code,
					},
				},
			},
			simType,
		});

		return Result.Success(accountId);
	};

	const fetchPlanSummary = async (
		products: (PlanProduct | DeliveryProduct | AddonProduct | BoosterProduct)[],
		promo: Campaign | null,
		{ skipVerification = false }: { skipVerification?: boolean } = {},
	) => {
		setIsFetchingSummary(true);

		const validPromo = await resolvePromo(promo, skipVerification);

		const deliveryProduct =
			simType === SIMTypeEnum.PLASTIC ? deliveryMethod : null;
		const selectedDeliveryMethod = deliveryProduct ? [deliveryProduct.id] : [];

		const planSummaryResult = await clientRef.getPlanSummary(
			[
				selectedProduct,
				...products.map((p) => p.id),
				...selectedDeliveryMethod,
			],
			validPromo,
			undefined,
			getSimChannelFromURL(),
			simType,
			getSimProductIdFromUrl(),
			true,
		);

		if (!planSummaryResult.isOk()) {
			setIsFetchingSummary(false);
			return planSummaryResult;
		}

		setPromoCode(validPromo);
		setPlanSummary(planSummaryResult.getSuccess());
		setIsFetchingSummary(false);

		return planSummaryResult;
	};

	const resolvePromo = async (
		promo: Campaign | null,
		skipVerification: boolean,
	): Promise<Campaign | null> => {
		if (!promo || !selectedProduct) return null;
		if (skipVerification) return promo;

		const campaignResult = await clientRef.verifyPromoCode({
			code: promo.code,
			event: "BUY",
			simChannel: "NON_RETAIL",
			type: "PHYSICAL",
			productId: selectedProduct,
		});

		return campaignResult.isOk() ? promo : null;
	};

	const isCampaignApplied = (campaign: Campaign) =>
		!!(promoCode && promoCode.id === campaign.id);

	const checkPromoCodeAndApply = async (promocode: string) => {
		const campaignResult = await clientRef.verifyPromoCode({
			code: promocode,
			event: "BUY",
			simChannel: "NON_RETAIL",
			type: "PHYSICAL",
		});

		if (campaignResult.isOk()) {
			const campaign = campaignResult.getSuccess();
			if (!isCampaignApplied(campaign)) {
				await fetchPlanSummary(Object.values(selectedProducts), campaign, {
					skipVerification: true,
				});
			}
		}

		setPromoCodeApplied(true);
		return campaignResult;
	};

	const removePromoCode = () => {
		setPromoCode(null);
		fetchPlanSummary(Object.values(selectedProducts), null);
	};

	const fetchProductData = async () => {
		const productsResult = await getProducts();
		if (!productsResult.isOk()) {
			return Result.Failure(new Error("Failed to fetch products"));
		}

		const fetchedProducts = productsResult.getSuccess();
		if (fetchedProducts.length === 0) {
			return Result.Failure(new Error("Failed to fetch plan summary"));
		}

		const {
			planProducts,
			addonProducts,
			boosterProducts,
			deliveryProducts: fetchedDeliveryProducts,
		} = ProductGrouper.GroupByType(fetchedProducts);

		const selectedPlan = planProducts.find(
			(product) => product?.responseData?.id === Number(selectedProduct),
		);
		if (!selectedPlan) {
			return Result.Failure(new Error("Failed to find selected plan"));
		}

		setPlan(selectedPlan);

		const [fetchedDeliveryProduct] = fetchedDeliveryProducts;
		setDeliveryMethod(fetchedDeliveryProduct);
		setProducts([...addonProducts, ...boosterProducts]);
		setIsHydrated(true);

		return Result.Success(true);
	};

	// Extract promo code from URL params (synchronous, no Branch SDK dependency)
	const getPromoCodeFromUrl = (): string | null => {
		if (typeof window === "undefined") return null;

		// Check felix_app_custom_data query param
		const params = new URLSearchParams(window.location.search);
		const felixAppCustomData = params.get("felix_app_custom_data");
		if (felixAppCustomData) {
			const parseResult = safeJsonParse<Record<string, unknown>>(
				felixAppCustomData,
				{},
			);
			if (
				parseResult.success &&
				parseResult.value &&
				typeof parseResult.value === "object" &&
				"code" in parseResult.value &&
				typeof parseResult.value.code === "string" &&
				parseResult.value.code.trim().length > 0
			) {
				return parseResult.value.code.trim();
			}
		}

		// Check referral code (rc=CODEendrc)
		if (window.location.search.includes("rc=")) {
			const rcMatch = window.location.href.match(/rc=(.*)endrc/);
			if (rcMatch?.[1]) {
				return rcMatch[1];
			}
		}

		// Check Branch deep link promo code (stored by getPlanIdFromBranch
		// in funnel.js when it reads the Branch session data).
		if (
			window.__branchPromoCode &&
			typeof window.__branchPromoCode === "string"
		) {
			return window.__branchPromoCode;
		}

		return null;
	};

	// Effect 1: Fetch products when selectedProduct is resolved
	useEffect(() => {
		if (selectedProduct === null) {
			return;
		}
		fetchProductData();
	}, [selectedProduct]);

	// Effect 2: Fetch plan summary once products are loaded (deliveryMethod is set by fetchProductData).
	// On the first run, resolves any promo code from URL params and includes it in the call.
	// selectedProduct is intentionally omitted — Effect 1 already watches it and
	// always sets deliveryMethod on change, so Effect 2 fires exactly once per plan
	// switch via the deliveryMethod dependency. The closure still captures the
	// latest selectedProduct value because the re-render from setDeliveryMethod
	// creates a fresh closure.
	// biome-ignore lint/correctness/useExhaustiveDependencies: promoCode and selectedProduct are intentionally excluded — see comment above
	useEffect(() => {
		if (selectedProduct === null || !deliveryMethod?.responseData?.id) {
			return;
		}

		const loadPlanSummary = async () => {
			let promo: Campaign | null;
			let skipVerification = false;

			if (promoCode) {
				// Existing promo — must re-verify against the (possibly new) plan
				promo = promoCode;
				skipVerification = false;
			} else {
				// First load — resolve from URL; already verified inside resolveUrlPromo
				promo = await resolveUrlPromo();
				skipVerification = !!promo;
			}

			fetchPlanSummary(Object.values(selectedProducts), promo, {
				skipVerification,
			});
		};

		const resolveUrlPromo = async (): Promise<Campaign | null> => {
			const urlCode = getPromoCodeFromUrl();
			if (!urlCode) return null;

			const campaignResult = await clientRef.verifyPromoCode({
				code: urlCode,
				event: "BUY",
				simChannel: "NON_RETAIL",
				type: "PHYSICAL",
				productId: selectedProduct,
			});
			if (!campaignResult.isOk()) return null;

			setPromoCodeApplied(true);
			return campaignResult.getSuccess();
		};

		loadPlanSummary();
	}, [JSON.stringify(selectedProducts), deliveryMethod]);

	return (
		<BuyContext.Provider
			value={{
				fetchPlanSummary,
				plan,
				products,
				planSummary,
				onboard,
				selectProduct,
				deselectProduct,
				selectedProducts: Object.values(selectedProducts),
				isCampaignApplied,
				checkPromoCodeAndApply,
				promoCodeApplied,
				removePromoCode,
				webBuyStart,
				simType,
				isEsimSelected,
				selectSIMType,
				isFetchingSummary,
				setFormStartedEventFired,
				formStartedEventFired,
			}}
		>
			{children}
		</BuyContext.Provider>
	);
};

export const useBuyContext = () => {
	const context = useContext(BuyContext);

	if (context === undefined) {
		throw new Error(
			"BuyContext.Consumer must be used within a BuyContext.Provider",
		);
	}

	return context;
};

export const withBuyContextProvider = (WrappedComponent: ComponentType) => {
	return class extends React.Component {
		render() {
			return (
				<BuyContextProvider>
					<WrappedComponent {...this.props} />
				</BuyContextProvider>
			);
		}
	};
};
